import React, { useState, useEffect } from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';

const C = {
  active400:    '#52c41a',
  active500:    '#389e0d',
  active700:    '#135200',
  activeMuted:  '#d9f7be',
  purple500:    '#9333ea',
  redMuted:     '#fff1f0',
  redText:      '#a8071a',
  cardBorder:   '#E2E8F0',
  pageBg:       '#F7FAFC',
  textPrimary:  '#1A1A1A',
  textSecondary:'#718096',
};

// ── Data ──────────────────────────────────────────────────────────────────────

const SENDERS = [
  {
    id: 'amazon', initials: 'A', name: 'Amazon',
    actionLabel: 'Archive', actionBg: C.activeMuted, actionColor: C.active700,
    ruleText: 'Older than 14 days',
    emails: ['Your order has shipped', 'Delivered: Echo Dot (5th Gen)', 'New deals based on your history', 'Your package is on the way'],
  },
  {
    id: 'linkedin', initials: 'in', name: 'LinkedIn',
    actionLabel: 'Delete', actionBg: C.redMuted, actionColor: C.redText,
    ruleText: 'Older than 30 days',
    emails: ['Alex M. wants to connect with you', 'Your profile got 14 views this week', 'Job alert: Product Designer @ Meta'],
  },
  {
    id: 'netflix', initials: 'N', name: 'Netflix',
    actionLabel: 'Archive', actionBg: C.activeMuted, actionColor: C.active700,
    ruleText: 'Older than 7 days',
    emails: ['New on Netflix: Stranger Things S5', 'Your monthly billing receipt'],
  },
];

const EMAIL_SEQUENCE = SENDERS.flatMap((s, si) =>
  s.emails.map((subject, ei) => ({ senderId: s.id, senderIdx: si, subject, ei }))
);

// ── Browser chrome ────────────────────────────────────────────────────────────

const BrowserChrome = () => (
  <Box bg="#3c3c3c" flexShrink={0} borderRadius="lg lg 0 0">
    <Flex align="center" px="10px" pt="8px" pb="0" gap="6px">
      <Flex gap="5px" align="center" mr="6px">
        <Box w="10px" h="10px" borderRadius="full" bg="#ff5f57" />
        <Box w="10px" h="10px" borderRadius="full" bg="#febc2e" />
        <Box w="10px" h="10px" borderRadius="full" bg="#28c840" />
      </Flex>
      <Flex bg="#525252" borderRadius="6px 6px 0 0" px="10px" py="5px" align="center" gap="5px" maxW="200px">
        <Flex w="12px" h="12px" borderRadius="sm" bg="#EA4335" align="center" justify="center" flexShrink={0}>
          <Text fontSize="8px" color="white" fontWeight="bold" lineHeight={1}>M</Text>
        </Flex>
        <Text fontSize="10px" color="white" noOfLines={1} opacity={0.9}>Inbox (3,500) — Gmail</Text>
      </Flex>
    </Flex>
    <Flex bg="#2a2a2a" mx="10px" mb="8px" borderRadius="md" px="10px" py="4px" align="center" gap="6px">
      <Text fontSize="10px" color="gray.500">← → ↻</Text>
      <Flex flex={1} bg="#4a4a4a" borderRadius="sm" px="8px" py="2px" align="center" gap="4px">
        <Text fontSize="9px" color="#6fbf73">🔒</Text>
        <Text fontSize="10px" color="gray.300">mail.google.com/mail/u/0/#inbox</Text>
      </Flex>
    </Flex>
  </Box>
);

// ── Inbox ─────────────────────────────────────────────────────────────────────

const EmailRow = ({ senderName, subject, isFirstOfSender, isVisible, isCleaned }) => {
  // Row takes space only when visible AND not cleaned
  const expanded = isVisible && !isCleaned;
  return (
    <Box
      overflow="hidden"
      style={{
        maxHeight:  expanded ? '36px' : '0px',
        opacity:    expanded ? 1 : 0,
        transition: 'max-height 0.5s ease, opacity 0.4s ease',
      }}
    >
      <Flex px="14px" py="6px" borderBottom="1px solid #f0f0f0" align="center" gap="8px" bg="white">
        <Box w="6px" h="6px" borderRadius="full" flexShrink={0} bg={isFirstOfSender ? C.purple500 : 'transparent'} />
        <Text fontSize="10px" fontWeight={isFirstOfSender ? 'bold' : 'normal'} color={C.textPrimary} w="56px" flexShrink={0} noOfLines={1}>
          {senderName}
        </Text>
        <Text fontSize="10px" color={C.textSecondary} flex={1} noOfLines={1}>{subject}</Text>
      </Flex>
    </Box>
  );
};

const InboxPanel = ({ visibleEmails, cleanedSenders, isDone }) => {
  const allClean = isDone && cleanedSenders.length === SENDERS.length;
  const remaining = visibleEmails.filter(e => !cleanedSenders.includes(e.senderId)).length;

  return (
    <Box display="flex" flexDirection="column" bg="white" h="100%">
      <Flex px="14px" py="8px" borderBottom="1px solid" borderColor={C.cardBorder}
        align="center" justify="space-between" flexShrink={0} bg="#f6f8fc">
        <Text fontSize="12px" fontWeight="bold" color={C.textPrimary}>Inbox</Text>
        <Text fontSize="11px" fontWeight="bold"
          color={allClean ? C.active500 : '#e53e3e'}
          style={{ transition: 'color 0.5s ease' }}>
          {allClean ? '✓ 0' : remaining > 0 ? remaining : ''}
        </Text>
      </Flex>

      <Box flex={1} overflowY="hidden" position="relative">
        {/* Empty state */}
        <Flex position="absolute" inset={0} direction="column" align="center" justify="center" gap="4px"
          opacity={allClean ? 1 : 0} style={{ transition: 'opacity 0.7s ease' }}
          pointerEvents="none" zIndex={1} bg="white">
          <Text fontSize="22px" color={C.active400}>✓</Text>
          <Text fontSize="11px" color={C.active700} fontWeight="semibold">All cleaned up</Text>
        </Flex>

        {EMAIL_SEQUENCE.map((email) => (
          <EmailRow
            key={`${email.senderId}-${email.ei}`}
            senderName={SENDERS[email.senderIdx].name}
            subject={email.subject}
            isFirstOfSender={email.ei === 0}
            isVisible={visibleEmails.some(e => e.senderId === email.senderId && e.ei === email.ei)}
            isCleaned={cleanedSenders.includes(email.senderId)}
          />
        ))}
      </Box>
    </Box>
  );
};

// ── Extension popup ───────────────────────────────────────────────────────────

const RuleCard = ({ sender, isVisible, isCleaned }) => (
  <Box overflow="hidden"
    style={{ maxHeight: isVisible ? '66px' : '0px', opacity: isVisible ? 1 : 0, transition: 'max-height 0.5s ease, opacity 0.45s ease' }}>
    <Box px="12px" py="9px" borderBottom="1px solid" borderColor={C.cardBorder}
      bg={isCleaned ? '#f6ffed' : 'white'} style={{ transition: 'background-color 0.5s ease' }}>
      <Flex align="center" gap="8px">
        <Flex w="24px" h="24px" borderRadius="full" bg="gray.100" color="gray.500"
          align="center" justify="center" fontSize="8px" fontWeight="bold" flexShrink={0}>
          {sender.initials}
        </Flex>
        <Box flex={1} minW={0}>
          <Text fontSize="10px" fontWeight="semibold" color={C.textPrimary} noOfLines={1}>{sender.name}</Text>
          <Flex align="center" gap="4px" mt="1px">
            <Text fontSize="9px" color={C.textSecondary}>{sender.ruleText} →</Text>
            <Box as="span" bg={sender.actionBg} color={sender.actionColor}
              fontSize="8px" fontWeight="bold" px="4px" py="1px" borderRadius="sm">
              {sender.actionLabel}
            </Box>
          </Flex>
        </Box>
        <Text fontSize="11px" fontWeight="bold" flexShrink={0}
          color={isCleaned ? C.active500 : '#ccc'} style={{ transition: 'color 0.4s ease' }}>
          {isCleaned ? '✓' : '●'}
        </Text>
      </Flex>
    </Box>
  </Box>
);

const ExtensionPopup = ({ visibleRules, isCleaning, isDone, progress, cleanedSenders }) => (
  <Box bg="white" display="flex" flexDirection="column" h="100%">
    {/* Header */}
    <Flex px="12px" py="9px" bg="#1a1a1a" align="center" justify="space-between" flexShrink={0}>
      <Flex align="center" gap="7px">
        <Box as="img" src="/images/logo.png" w="18px" h="18px" borderRadius="sm" flexShrink={0}
          style={{ objectFit: 'contain' }} />
        <Text fontSize="11px" color="white" fontWeight="semibold">Email Cleaner</Text>
      </Flex>
      <Text fontSize="8px" color="gray.600" letterSpacing="0.05em">EXTENSION</Text>
    </Flex>

    {/* Progress */}
    <Box px="12px" py="9px" borderBottom="1px solid" borderColor={C.cardBorder} flexShrink={0}>
      <Flex align="center" justify="space-between" mb="7px">
        <Text fontSize="9px" color={C.textSecondary}>
          {isDone ? 'Inbox is clean' : isCleaning ? 'Cleaning…' : visibleRules.length > 0 ? `${visibleRules.length} rule${visibleRules.length > 1 ? 's' : ''} detected` : 'Watching inbox…'}
        </Text>
        <Box as="button"
          bg={isDone ? C.activeMuted : isCleaning ? C.activeMuted : visibleRules.length > 0 ? C.active500 : 'gray.100'}
          color={isDone ? C.active700 : isCleaning ? C.active700 : visibleRules.length > 0 ? 'white' : C.textSecondary}
          fontSize="9px" fontWeight="bold" px="8px" py="3px" borderRadius="md" border="none"
          cursor={visibleRules.length > 0 ? 'pointer' : 'default'} whiteSpace="nowrap"
          style={{ transition: 'background 0.4s ease, color 0.4s ease' }}>
          {isDone ? '✓ Done' : isCleaning ? '⏸ Pause' : 'Start cleaning'}
        </Box>
      </Flex>
      <Box h="4px" borderRadius="full" bg="gray.100" overflow="hidden">
        <Box h="100%" bg={C.active400} w={`${progress * 100}%`} style={{ transition: 'width 1s ease' }} />
      </Box>
    </Box>

    {/* Rules */}
    <Box flex={1} overflowY="hidden">
      {SENDERS.map(s => (
        <RuleCard key={s.id} sender={s}
          isVisible={visibleRules.includes(s.id)}
          isCleaned={cleanedSenders.includes(s.id)} />
      ))}
    </Box>
  </Box>
);

// ── Main ──────────────────────────────────────────────────────────────────────

export default function ExtensionDemo() {
  const [visibleEmails,  setVisibleEmails]  = useState([]);
  const [visibleRules,   setVisibleRules]   = useState([]);
  const [cleanedSenders, setCleanedSenders] = useState([]);
  const [isCleaning,     setIsCleaning]     = useState(false);
  const [isDone,         setIsDone]         = useState(false);
  const [progress,       setProgress]       = useState(0);

  useEffect(() => {
    const timers = [];
    const t = (ms, fn) => { const id = setTimeout(fn, ms); timers.push(id); };

    const runCycle = () => {
      setVisibleEmails([]); setVisibleRules([]); setCleanedSenders([]);
      setIsCleaning(false); setIsDone(false); setProgress(0);

      EMAIL_SEQUENCE.forEach((email, i) => {
        t(800 + i * 500, () => {
          setVisibleEmails(prev => [...prev, { senderId: email.senderId, ei: email.ei }]);
          if (email.ei === 0)
            setVisibleRules(prev => prev.includes(email.senderId) ? prev : [...prev, email.senderId]);
        });
      });

      const allIn = 800 + (EMAIL_SEQUENCE.length - 1) * 500; // ~5300ms

      t(allIn + 900,  () => { setIsCleaning(true); setProgress(0.05); });
      t(allIn + 1400, () => { setCleanedSenders(['amazon']);                        setProgress(0.33); });
      t(allIn + 2900, () => { setCleanedSenders(['amazon', 'linkedin']);             setProgress(0.66); });
      t(allIn + 4400, () => { setCleanedSenders(['amazon', 'linkedin', 'netflix']);  setProgress(1);    });
      t(allIn + 5300, () => { setIsCleaning(false); setIsDone(true); });
    };

    runCycle();
    const loop = setInterval(runCycle, 17000);
    return () => { timers.forEach(clearTimeout); clearInterval(loop); };
  }, []);

  const BROWSER_H = 370; // px — slightly taller (more bottom space)
  const POPUP_H   = 300; // px
  const POPUP_W   = 220; // px
  const PEEK      = 60;  // how many px the popup rises above the browser top

  return (
    <Box
      w="100%" maxW="660px"
      position="relative"
      pt={`${PEEK}px`}   // push browser down so popup can float above
      aria-hidden="true"
    >
      {/* ── Browser window ── */}
      <Box
        bg="#d1d5db"
        borderRadius="xl"
        p="10px"
        shadow="xl"
        h={`${BROWSER_H}px`}
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <BrowserChrome />
        <Box flex={1} overflow="hidden" borderRadius="0 0 lg lg">
          <InboxPanel
            visibleEmails={visibleEmails}
            cleanedSenders={cleanedSenders}
            isDone={isDone}
          />
        </Box>
      </Box>

      {/* ── Extension popup — floats top-right, partly above browser ── */}
      <Box
        position="absolute"
        top="0"
        right="-24px"
        w={`${POPUP_W}px`}
        h={`${POPUP_H}px`}
        borderRadius="lg"
        overflow="hidden"
        shadow="2xl"
        border="1px solid"
        borderColor="gray.300"
        zIndex={10}
      >
        <ExtensionPopup
          visibleRules={visibleRules}
          isCleaning={isCleaning}
          isDone={isDone}
          progress={progress}
          cleanedSenders={cleanedSenders}
        />
      </Box>
    </Box>
  );
}
